import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api } from "@/lib/api";
import {
  property as defaultProperty,
  highlights as defaultHighlights,
  owners as defaultOwners,
  reviews as defaultReviews,
} from "@/data/content";

const ContentContext = createContext(null);

// Defaults — used until an admin edit is saved to the database.
export const DEFAULTS = {
  tagline: defaultProperty.tagline,
  location: defaultProperty.location,
  shortDescription: defaultProperty.shortDescription,
  longAbout: defaultProperty.longAbout,
  highlights: defaultHighlights,
  ownersTitle: defaultOwners.title,
  ownersBody: defaultOwners.body,
  reviews: defaultReviews,
  nearby: [
    { title: "The beach", body: "50 m · 1 minute walk" },
    {
      title: "Pefkochori centre",
      body: "5 min walk · tavernas, bakeries, shops",
    },
    {
      title: "Thessaloniki airport (SKG)",
      body: "≈ 1h 30min by car",
    },
  ],
  houseNotes: [
    "Quiet hours 23:00 — 08:00",
    "Check-in 16:00 · Check-out 11:00",
    "Pet-friendly on request",
  ],
};

function merge(remote) {
  // Each top-level key overrides defaults if set in remote.
  return { ...DEFAULTS, ...(remote || {}) };
}

export function ContentProvider({ children }) {
  const [content, setContent] = useState(merge());
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/content");
      setContent(merge(res.data));
    } catch (e) {
      setContent(merge());
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ content, loaded, refresh, setContent }),
    [content, loaded, refresh]
  );
  return (
    <ContentContext.Provider value={value}>{children}</ContentContext.Provider>
  );
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) {
    // Safe fallback if used outside provider (e.g., during early tests)
    return { content: merge(), loaded: true, refresh: async () => {} };
  }
  return ctx;
}
